// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/summary-strip.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import { Item } from "@repo/design-system/components/ui/item";
import { Card } from "@repo/design-system/components/ui/v2/card";
import { cn } from "@repo/design-system/lib/utils";
import { ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type { ComponentProps } from "react";

/**
 * v2 Summary Strip — the stats bar at the top of dashboard pages ("Total
 * contractors: 42"), per the Figma "Remote Leverage — Design System" file,
 * "Summary Strip" page `223:19184`: the strip set `223:19255` (Cards=3 /
 * Cards=4 / Cards=5) and its "Card" item `223:19259`. No v1 equivalent —
 * a composite of v2 `Card` (the strip container) and v1 `Item` (each stat).
 *
 * Exact from Figma:
 * - Strip: white, 16px radius, clipped, items laid out in one row at equal
 *   widths (`flex-1`), separated by a full-height 1px rule that takes no
 *   layout width. The Cards=3/4/5 variants are just the item count, so
 *   there is no `cards` prop — render as many `SummaryStripItem`s as needed.
 * - Item: 24px/16px padding, 4px between rows. Label is 14px regular
 *   (--text/tertiary), value 30px medium at 1.32 line-height
 *   (--text/primary), optional caption 12px medium (--text/secondary).
 * - Figma's `showIcon` → `SummaryStripLabel`'s `showIcon`: a 16px
 *   ChevronRight pushed to the label row's end (lucide's `ChevronRight` is
 *   the same path, and its default stroke renders Figma's 1.33px at 16px).
 * - Figma's `showLabel` → `SummaryStripCaption` (the third, smaller line).
 *
 * Interpolated (the ticket asks for it, Figma doesn't draw it):
 * - Trend indicator: `SummaryStripTrend`, a lucide TrendingUp/TrendingDown/
 *   Minus icon plus text, set in the caption line's own type style and
 *   colored with v2's existing status green (badge green text) / error red
 *   (input/button destructive) / --text/secondary. Place it inside a
 *   `SummaryStripCaption` to pair it with a comparison ("+12% vs last
 *   month"), or on its own. `tone` defaults from `direction` (up → positive,
 *   down → negative) and can be overridden for metrics where up is bad
 *   (e.g. overdue invoices). The icon is decorative — the text should carry
 *   the sign.
 * - Responsive wrap: the strip wraps; each item grows from a 200px basis
 *   (`--v2-summary-strip-item-min-width`, just under Figma's 1088px / 5
 *   cards so every drawn variant stays on one row at its drawn width), and
 *   the last row's items stretch to fill it. Dividers are drawn as a 1px
 *   left + top box-shadow on every item, which the clipped container hides
 *   on the first column and first row — so the same rule appears between
 *   columns and between wrapped rows without per-breakpoint logic.
 * - Clickable items: `SummaryStripItem` keeps v1 Item's `asChild`, so an
 *   item can render as an `<a>`/`<button>` (the chevron's implied drill-in).
 *   Hover and focus reuse v2 Card's hover fill and focus-ring color; the
 *   ring is inset because the strip clips its children. The container's own
 *   Card hover/focus-within treatment is turned off — the strip itself is
 *   not interactive, and a second ring around the whole strip would be
 *   noise next to the focused item's.
 * - Label and value truncate rather than overflow when space runs out.
 * - The strip never outgrows its parent: `max-w-full` clamps a fixed
 *   consumer width (e.g. Figma's `w-[1088px]` in a narrower column) so
 *   items wrap instead, and `min-w-0` lets it shrink inside a flex/grid
 *   parent instead of holding its items' min-content width.
 */

function SummaryStrip({ className, ...props }: ComponentProps<typeof Card>) {
  return (
    <Card
      className={cn(
        "min-w-0 max-w-full flex-row flex-wrap items-stretch gap-0 overflow-hidden border-0 p-0",
        "focus-within:shadow-none hover:bg-card",
        className
      )}
      data-slot="summary-strip"
      {...props}
    />
  );
}

function SummaryStripItem({
  className,
  ...props
}: Omit<ComponentProps<typeof Item>, "variant" | "size">) {
  return (
    <Item
      className={cn(
        "ds-v2 min-w-0 grow basis-(--v2-summary-strip-item-min-width) flex-col flex-nowrap items-stretch gap-1 rounded-none border-0 px-6 py-4 text-left",
        "shadow-[-1px_0_0_0_var(--v2-summary-strip-divider),0_-1px_0_0_var(--v2-summary-strip-divider)]",
        "[a]:hover:bg-(--v2-summary-strip-item-bg-hover) [button]:hover:bg-(--v2-summary-strip-item-bg-hover)",
        "focus-visible:inset-ring-(--v2-summary-strip-focus-ring) focus-visible:inset-ring-3 focus-visible:ring-0",
        className
      )}
      data-slot="summary-strip-item"
      size="sm"
      {...props}
    />
  );
}

type SummaryStripLabelProps = ComponentProps<"div"> & {
  /** Figma's `showIcon` — a trailing chevron hinting the item drills in. */
  showIcon?: boolean;
};

function SummaryStripLabel({
  className,
  children,
  showIcon = false,
  ...props
}: SummaryStripLabelProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center justify-between gap-2 text-(--v2-summary-strip-label) text-sm leading-[1.56]",
        className
      )}
      data-slot="summary-strip-label"
      {...props}
    >
      <span className="truncate">{children}</span>
      {showIcon ? (
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-(--v2-summary-strip-icon)"
        />
      ) : null}
    </div>
  );
}

function SummaryStripValue({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "truncate font-medium text-(--v2-summary-strip-value) text-[30px] leading-[1.32]",
        className
      )}
      data-slot="summary-strip-value"
      {...props}
    />
  );
}

function SummaryStripCaption({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-1 font-medium text-(--v2-summary-strip-caption) text-xs leading-[1.56]",
        className
      )}
      data-slot="summary-strip-caption"
      {...props}
    />
  );
}

const TREND_ICONS = {
  up: TrendingUp,
  down: TrendingDown,
  flat: Minus,
} as const;

const TREND_TONE_CLASSES = {
  positive: "text-(--v2-summary-strip-trend-positive)",
  negative: "text-(--v2-summary-strip-trend-negative)",
  neutral: "text-(--v2-summary-strip-trend-neutral)",
} as const;

const DEFAULT_TONE = {
  up: "positive",
  down: "negative",
  flat: "neutral",
} as const;

type SummaryStripTrendDirection = keyof typeof TREND_ICONS;
type SummaryStripTrendTone = keyof typeof TREND_TONE_CLASSES;

type SummaryStripTrendProps = ComponentProps<"span"> & {
  direction: SummaryStripTrendDirection;
  /** Defaults from `direction`; override when "up" is bad news. */
  tone?: SummaryStripTrendTone;
};

function SummaryStripTrend({
  className,
  children,
  direction,
  tone = DEFAULT_TONE[direction],
  ...props
}: SummaryStripTrendProps) {
  const Icon = TREND_ICONS[direction];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 font-medium text-xs leading-[1.56]",
        TREND_TONE_CLASSES[tone],
        className
      )}
      data-direction={direction}
      data-slot="summary-strip-trend"
      data-tone={tone}
      {...props}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      {children}
    </span>
  );
}

export {
  SummaryStrip,
  SummaryStripCaption,
  SummaryStripItem,
  SummaryStripLabel,
  SummaryStripTrend,
  SummaryStripValue,
};
export type {
  SummaryStripLabelProps,
  SummaryStripTrendDirection,
  SummaryStripTrendProps,
  SummaryStripTrendTone,
};
